import { useMemo, useState } from "react"
import { Calendar, Download, FileText, Loader2, Printer } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { useTimesheetReport } from "@/hooks/useTimesheetRecords"
import { useAuth } from "@/hooks/useAuth"

const reportTypes = [
  { value: "folha", label: "Folha de Ponto" },
  { value: "espelho", label: "Espelho de Ponto" },
  { value: "horas_extras", label: "Horas Extras" },
  { value: "banco_horas", label: "Banco de Horas" },
  { value: "absenteismo", label: "Absenteismo" },
  { value: "atividades", label: "Atividades Realizadas" },
  { value: "ocorrencias", label: "Ocorrencias" },
  { value: "ponto_localizacao", label: "Ponto com Localizacao" },
]

const toLocalYMD = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const formatYMDToBR = (ymd: string) => {
  if (!ymd) return "-"
  const [year, month, day] = ymd.split("-")
  return `${day}/${month}/${year}`
}

const formatTime = (date: string | Date | null) => {
  if (!date) return "-"
  return new Date(date).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })
}

const intervalToMinutes = (interval: string | null) => {
  if (!interval) return 0
  const match = interval.match(/(\d+):(\d+):(\d+)/)
  if (!match) return 0
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10)
}

const formatMinutes = (minutes: number) => {
  const sign = minutes < 0 ? "-" : ""
  const abs = Math.abs(minutes)
  const hours = Math.floor(abs / 60)
  const mins = abs % 60
  return `${sign}${hours}h ${String(mins).padStart(2, "0")}m`
}

export const TimesheetReport = () => {
  const [open, setOpen] = useState(false)
  const [reportType, setReportType] = useState("espelho")
  const [detailed, setDetailed] = useState(true)
  const [showSchedule, setShowSchedule] = useState(true)
  const [showDelays, setShowDelays] = useState(true)
  const { profile } = useAuth()

  const now = new Date()
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const [startDate, setStartDate] = useState(toLocalYMD(firstDayOfMonth))
  const [endDate, setEndDate] = useState(toLocalYMD(now))

  const { data: rows = [], isLoading, error } = useTimesheetReport(startDate, endDate)

  const summary = useMemo(() => {
    const expectedPerDay = 8 * 60
    const worked = rows.reduce((total, day) => total + intervalToMinutes(day.total_horas), 0)
    const workedDays = rows.filter((day) => intervalToMinutes(day.total_horas) > 0).length
    const expected = workedDays * expectedPerDay
    const balance = worked - expected
    const absences = rows.filter((day) => !day.entrada && !day.saida).length
    const incomplete = rows.filter((day) => {
      const marks = [day.entrada, day.saida_almoco, day.volta_almoco, day.saida].filter(Boolean).length
      return marks > 0 && marks % 2 !== 0
    }).length

    return {
      worked,
      expected,
      balance,
      absences,
      incomplete,
      workedDays,
    }
  }, [rows])

  const selectedReportLabel = reportTypes.find((item) => item.value === reportType)?.label || "Relatorio"

  const exportJson = () => {
    const payload = {
      type: reportType,
      label: selectedReportLabel,
      period: { startDate, endDate },
      employee: {
        id: profile?.id,
        name: profile?.full_name,
        email: profile?.email,
      },
      summary,
      rows,
      generatedAt: new Date().toISOString(),
      complianceNotice: "Base operacional. AFD/AEJ e assinaturas ICP-Brasil ainda devem ser implementados para conformidade fiscal completa.",
    }

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${reportType}-${startDate}-${endDate}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const printReport = () => {
    window.print()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-center">
          <FileText className="h-4 w-4 mr-2" />
          Central de Relatorios
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Relatorios do Ponto Digital
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-lg border bg-muted/20 p-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reportTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Inicio</Label>
              <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Fim</Label>
              <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={exportJson} variant="outline" className="flex-1" disabled={rows.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
              <Button onClick={printReport} variant="outline" disabled={rows.length === 0}>
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox checked={showSchedule} onCheckedChange={(value) => setShowSchedule(Boolean(value))} />
              Exibir expediente da escala
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={showDelays} onCheckedChange={(value) => setShowDelays(Boolean(value))} />
              Exibir atrasos
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={detailed} onCheckedChange={(value) => setDetailed(Boolean(value))} />
              Modelo detalhado
            </label>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Trabalhadas</p>
              <p className="text-lg font-semibold">{formatMinutes(summary.worked)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Previstas</p>
              <p className="text-lg font-semibold">{formatMinutes(summary.expected)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Banco</p>
              <p className={summary.balance >= 0 ? "text-lg font-semibold text-emerald-600" : "text-lg font-semibold text-rose-600"}>
                {formatMinutes(summary.balance)}
              </p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Ausencias</p>
              <p className="text-lg font-semibold">{summary.absences}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Inconsistencias</p>
              <p className="text-lg font-semibold">{summary.incomplete}</p>
            </div>
          </div>

          <div className="rounded-lg border p-4 print:border-black">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold">{selectedReportLabel}</h3>
                <p className="text-sm text-muted-foreground">
                  Apuracao: {formatYMDToBR(startDate)} a {formatYMDToBR(endDate)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Colaborador: {profile?.full_name || "Usuario atual"}
                </p>
              </div>
              <Badge variant="outline">Base operacional</Badge>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center p-10">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Carregando dados...
              </div>
            ) : error ? (
              <div className="text-center p-8 text-rose-600">
                Erro ao carregar dados: {error.message}
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                Nenhum registro encontrado no periodo selecionado.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dia</TableHead>
                    {showSchedule && <TableHead>Previstas</TableHead>}
                    <TableHead>Entrada</TableHead>
                    <TableHead>Saida Almoco</TableHead>
                    <TableHead>Volta Almoco</TableHead>
                    <TableHead>Saida</TableHead>
                    <TableHead>Trabalhadas</TableHead>
                    {showDelays && <TableHead>Status</TableHead>}
                    {detailed && <TableHead>Observacoes</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((day, index) => {
                    const worked = intervalToMinutes(day.total_horas)
                    const status = worked === 0 ? "Ausente" : worked < 8 * 60 ? "Parcial" : "Completo"
                    return (
                      <TableRow key={`${day.date}-${index}`}>
                        <TableCell>{formatYMDToBR(day.date)}</TableCell>
                        {showSchedule && <TableCell>08h 00m</TableCell>}
                        <TableCell>{formatTime(day.entrada)}</TableCell>
                        <TableCell>{formatTime(day.saida_almoco)}</TableCell>
                        <TableCell>{formatTime(day.volta_almoco)}</TableCell>
                        <TableCell>{formatTime(day.saida)}</TableCell>
                        <TableCell>{formatMinutes(worked)}</TableCell>
                        {showDelays && <TableCell>{status}</TableCell>}
                        {detailed && <TableCell>{day.observacoes || "-"}</TableCell>}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
